import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import { useRef } from 'react';
import { fetchMAffiliateSettingsApi } from '@/api/infoApi';
import { registerPaidyTokenApi } from '@/api/payment';
import { postCreateGmoLinkTypePlusCardParameterApi } from '@/api/prepaidApi';
import {
  getSubscriptionStatusApi,
  postSubscriptionInitialPaymentApi,
  postSubscriptionActivateApi,
} from '@/api/subscription';
import { PostChargeDataToGmoForm } from '@/components/pages/ChargePrepaid/PostChargeDataToGmoForm/PostChargeDataToGmoForm';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { isApp } from '@/config/device';
import { initialState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { endpoint } from '@/consts/api';
import { paymentTypeId } from '@/consts/payment';
import { useGmo } from '@/hooks/useGmo';
import { usePaidy } from '@/hooks/usePaidy';
import { GmoChargeParams } from '@/types/api/payment';
import { SubscriptionStatus } from '@/types/api/subscription';
import { PAIDY_LOGO_URL } from '@/utils/payment';
import { storeDispatch } from '@/utils/store';

interface SubscriptionPaymentPageProps {
  subscriptionId?: number;
  planName?: string;
  monthlyFee?: number;
}

// 決済の進行ステップ
type PaymentStep = 'select' | 'initial_done' | 'all_done';

export const SubscriptionPaymentPage: FC<SubscriptionPaymentPageProps> = ({
  subscriptionId: propSubscriptionId,
  planName: propPlanName,
  monthlyFee: propMonthlyFee,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStep, setPaymentStep] = useState<PaymentStep>('select');
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(
    null,
  );
  const [gmoChargeParams, setGmoChargeParams] =
    useState<GmoChargeParams | null>(null);
  const gmoFormRef = useRef<HTMLFormElement>(null);
  const authInfo = store.state.authInfo as any;
  const paymentMethods = useStore(store, 'getPaymentMethodsInfo') as any[];
  const { launchPaidyCheckout } = usePaidy();
  const { addCreditCardPaymentMethod } = useGmo(paths.subscriptionPayment);

  const getPaymentType = (pm: any) =>
    Number(pm.paymentTypeId || pm.m_payment_type_id || pm.payment_type_id || 0);
  const registeredCards = (paymentMethods || []).filter(
    (pm: any) => getPaymentType(pm) === paymentTypeId.CREDIT,
  );
  const allPaidys = (paymentMethods || []).filter(
    (pm: any) => getPaymentType(pm) === paymentTypeId.PAIDY,
  );
  // 最新の1つだけ表示
  const registeredPaidys =
    allPaidys.length > 0 ? [allPaidys[allPaidys.length - 1]] : [];

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await getSubscriptionStatusApi(authInfo.m_customer_id);
        if (res.data.success && res.data.data?.subscription) {
          const sub = res.data.data.subscription;
          setSubscription(sub);
          // apply_status=2なら初回決済済み→トークン登録ステップから開始
          if ((sub as any).apply_status === 2) {
            setPaymentStep('initial_done');
          }
        }
      } catch (e) {
        console.error('契約状態取得エラー', e);
      }
    };
    if (authInfo?.m_customer_id) fetchStatus();
  }, [authInfo?.m_customer_id]);

  const displayFee =
    propMonthlyFee || subscription?.monthly_billing_amount || 0;
  const displayPlanName = propPlanName || subscription?.plan_name || '';

  const subscriptionId =
    propSubscriptionId || (subscription as any)?.t_subscription_id;

  // 登録済みPaidy: 都度決済→既存トークンで即有効化（token登録スキップ）
  const handleRegisteredPaidy = async (pm: any) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const methodId = pm.m_payment_method_id || pm.paymentMethodId;

      // Step1: Paidy Checkout（payment mode）で初回都度決済
      const paymentResult = await launchPaidyCheckout({
        mode: 'payment',
        amount: displayFee,
        orderRef: `sub_initial_${authInfo.m_customer_id}_${Date.now()}`,
        storeName: displayPlanName,
      });

      if (!paymentResult?.paymentId) {
        setIsProcessing(false);
        return;
      }

      // apply_status 1→2 に更新
      const res1 = await postSubscriptionInitialPaymentApi({
        t_subscription_id: subscriptionId,
        m_payment_type_id: paymentTypeId.PAIDY,
        m_payment_method_id: methodId,
      });
      if (!res1.data.success) {
        throw new Error(
          res1.data.error_message || '初回決済の記録に失敗しました',
        );
      }

      // Step2: 既存トークンで即有効化（apply_status 2→0）
      const res2 = await postSubscriptionActivateApi({
        t_subscription_id: subscriptionId,
        m_payment_type_id: paymentTypeId.PAIDY,
        m_payment_method_id: methodId,
      });
      if (!res2.data.success) {
        throw new Error(res2.data.error_message || '有効化に失敗しました');
      }

      setPaymentStep('all_done');
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 新規Paidyで初回都度決済
  const handleSelectPaidy = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // Step1: 初回都度決済（mode=payment）
      const paymentResult = await launchPaidyCheckout({
        mode: 'payment',
        amount: displayFee,
        orderRef: `sub_initial_${authInfo.m_customer_id}_${Date.now()}`,
        storeName: displayPlanName,
      });

      if (!paymentResult?.paymentId) {
        setIsProcessing(false);
        return;
      }

      // バックエンドで apply_status を 1→2 に更新
      // 新規Paidyの場合、まだpayment_method_idがないので0で送る
      if (subscriptionId) {
        try {
          await postSubscriptionInitialPaymentApi({
            t_subscription_id: subscriptionId,
            m_payment_type_id: paymentTypeId.PAIDY,
            m_payment_method_id: 0,
          });
        } catch (e) {
          console.error('apply_status更新エラー（継続）', e);
        }
      }

      // 都度決済完了 → トークン登録ステップ表示
      setPaymentStep('initial_done');
      setIsProcessing(false);
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
      setIsProcessing(false);
    }
  };

  // ステップ2: 継続決済方法登録 — クレカ or Paidy
  const handleActivateWithCard = async (card: any) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const methodId = card.m_payment_method_id || card.paymentMethodId;
      const activateRes = await postSubscriptionActivateApi({
        t_subscription_id: subscriptionId,
        m_payment_type_id: paymentTypeId.CREDIT,
        m_payment_method_id: methodId,
      });
      if (!activateRes.data.success) {
        throw new Error(
          activateRes.data.error_message || '有効化に失敗しました',
        );
      }
      setPaymentStep('all_done');
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleActivateWithPaidy = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      // 既にPaidyトークンがあればそれを使う
      if (registeredPaidys.length > 0) {
        const pm = registeredPaidys[registeredPaidys.length - 1];
        const methodId = pm.m_payment_method_id || pm.paymentMethodId;
        const activateRes = await postSubscriptionActivateApi({
          t_subscription_id: subscriptionId,
          m_payment_type_id: paymentTypeId.PAIDY,
          m_payment_method_id: methodId,
        });
        if (!activateRes.data.success) {
          throw new Error(
            activateRes.data.error_message || '有効化に失敗しました',
          );
        }
        setPaymentStep('all_done');
        return;
      }

      // 新規トークン登録
      const tokenResult = await launchPaidyCheckout({ mode: 'token' });
      if (!tokenResult?.tokenId) {
        setIsProcessing(false);
        return;
      }
      const registerRes = await registerPaidyTokenApi({
        m_customer_id: authInfo.m_customer_id,
        paidy_token_id: tokenResult.tokenId,
        customer_name: '',
        customer_email: '',
        phone_number: '',
      });
      if (!registerRes.data.success) {
        throw new Error('決済情報の保存に失敗しました');
      }
      const paidyMethodId = (registerRes.data as any).data?.id;
      const activateRes = await postSubscriptionActivateApi({
        t_subscription_id: subscriptionId,
        m_payment_type_id: paymentTypeId.PAIDY,
        m_payment_method_id: paidyMethodId,
      });
      if (!activateRes.data.success) {
        throw new Error(
          activateRes.data.error_message || '有効化に失敗しました',
        );
      }
      setPaymentStep('all_done');
    } catch (e: any) {
      f7.dialog.alert(e.message || 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClickHome = () => {
    f7.view.main.router.navigate(paths.home);
  };

  const handleSelectCard = async (card: any) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      f7.preloader.show();
      const { m_affiliate_id } = store.state.customerInfo as any;

      // 1. アフィリエイト設定からGMO情報取得
      const { data: settings } = await fetchMAffiliateSettingsApi({
        m_affiliate_id,
      });
      if (!settings.success) throw new Error('GMO設定の取得に失敗しました');
      const { gmopg_site_id, gmopg_site_pass, gmopg_shop_id, gmopg_shop_pass } =
        settings.data;

      const cardMethodId = card.m_payment_method_id || card.paymentMethodId;

      // 2. サブスク決済パラメータ生成
      const chargeParamRes = await postSubscriptionInitialPaymentApi({
        t_subscription_id: subscriptionId,
        m_payment_type_id: paymentTypeId.CREDIT,
        m_payment_method_id: cardMethodId,
      });
      // cardChargeParameter APIを呼ぶ（SubscriptionController経由）
      const paramRes = await import('@/config/axios').then(({ baseApi }) =>
        baseApi.post('/v1/customer/subscription/card-charge-parameter', {
          m_customer_id: authInfo.m_customer_id,
          m_subscription_plan_id:
            (subscription as any)?.m_subscription_plan_id || 1,
          m_payment_method_id: cardMethodId,
          amount: displayFee,
          ShopID: gmopg_shop_id,
          ShopPass: gmopg_shop_pass,
          siteID: gmopg_site_id,
          sitePass: gmopg_site_pass,
        }),
      );

      if (!paramRes.data.success)
        throw new Error('決済パラメータの生成に失敗しました');
      const { DateTime, OrderID, MemberID, ShopPassString, MemberPassString } =
        paramRes.data.data;

      // 3. GMO決済URL生成
      const completeUrl = `${endpoint.BASE_URL}/subscription/payment_success/`;
      const cancelUrl = `${endpoint.BASE_URL}/subscription/payment_cancel/`;

      const linkRes = await postCreateGmoLinkTypePlusCardParameterApi({
        m_affiliate_id: String(m_affiliate_id),
        m_customer_id: String(authInfo.m_customer_id),
        config_id: '1',
        order_id: OrderID,
        amount: String(displayFee),
        complete_url: completeUrl,
        cancel_url: cancelUrl,
        member_id: MemberID,
      });

      if (!linkRes.data.success) throw new Error('決済URL生成に失敗しました');

      // 4. LocalStorageにリダイレクト情報を保存
      window.localStorage.setItem('token', authInfo.token);
      window.localStorage.setItem(
        'm_customer_id',
        String(authInfo.m_customer_id),
      );
      window.localStorage.setItem('fromPage', paths.subscriptionPayment);

      // 5. GMOフォームPOSTで決済ページに遷移
      const params: GmoChargeParams = {
        ShopID: gmopg_shop_id,
        OrderID,
        Amount: displayFee,
        Tax: 0,
        DateTime,
        ShopPassString,
        JobCd: 'CAPTURE',
        SiteID: gmopg_site_id,
        MemberID,
        MemberPassString,
        UseCredit: '1',
        RetURL: completeUrl,
        CancelURL: cancelUrl,
      };
      setGmoChargeParams(params);

      // フォームをDOMに反映後にsubmit
      setTimeout(() => {
        gmoFormRef.current?.submit();
      }, 100);
    } catch (e: any) {
      f7.preloader.hide();
      f7.dialog.alert(e.message || 'エラーが発生しました');
      setIsProcessing(false);
    }
  };

  // 車検証登録済みかどうか（t_stock_car_idがあるか）
  const hasCarRegistered =
    !!(subscription as any)?.t_stock_car_id || !!(propSubscriptionId && false);
  const allComplete = hasCarRegistered;

  // ステップ3: 決済完了
  if (paymentStep === 'all_done') {
    return (
      <PageTemplate
        pageName="subscription-payment"
        title={displayPlanName}
        showNavBack
      >
        <div style={{ padding: '32px 16px', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#E3F2FD',
              borderRadius: '20px',
              fontSize: '13px',
              color: '#1E55E6',
              fontWeight: 'bold',
              marginBottom: '16px',
            }}
          >
            {allComplete ? 'サービス開始！' : 'お支払い完了！'}
          </div>

          <div
            style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 16px',
              borderRadius: '50%',
              backgroundColor: '#E8F5E9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: '#4CAF50',
            }}
          >
            ✓
          </div>

          <h2
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '8px',
            }}
          >
            お支払いが完了しました！
          </h2>
          {!allComplete && (
            <p
              style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}
            >
              サービス開始には車検証の登録が必要です
            </p>
          )}

          <div
            style={{
              padding: '20px',
              margin: '0 0 24px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>お申し込み</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>初回お支払い</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>
                来月以降のお支払い方法登録
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
              }}
            >
              {hasCarRegistered ? (
                <>
                  <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
                  <span style={{ fontSize: '14px' }}>車両の本登録</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '12px',
                      color: '#4CAF50',
                      fontWeight: 'bold',
                    }}
                  >
                    完了
                  </span>
                </>
              ) : (
                <>
                  <span style={{ color: '#FF9800', fontSize: '16px' }}>○</span>
                  <span style={{ fontSize: '14px' }}>車両の本登録</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: '#FFF3E0',
                      color: '#E65100',
                    }}
                  >
                    未完了
                  </span>
                </>
              )}
            </div>
          </div>

          {!hasCarRegistered && (
            <button
              onClick={() => {
                const affiliateId = (store.state as any).customerInfo
                  ?.m_affiliate_id;
                storeDispatch('setRegisterUser', 'registerUser', {
                  ...initialState.registerUser,
                  affiliate_id: Number(affiliateId),
                  currentStep: 3,
                });
                f7.view.main.router.navigate(paths.registerNewMember, {
                  props: {
                    sourcePage: paths.subscriptionPayment,
                    startStep: 3,
                  },
                });
              }}
              style={{
                width: '100%',
                padding: '16px',
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: '#1E55E6',
                border: 'none',
                borderRadius: '28px',
                cursor: 'pointer',
              }}
            >
              車検証を登録する
            </button>
          )}

          <button
            onClick={handleClickHome}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '14px',
              color: allComplete ? '#fff' : '#666',
              backgroundColor: allComplete ? '#1E55E6' : '#fff',
              border: allComplete ? 'none' : '1px solid #ccc',
              borderRadius: '28px',
              cursor: 'pointer',
            }}
          >
            {allComplete ? 'ホームに戻る' : 'あとで登録する'}
          </button>
        </div>
      </PageTemplate>
    );
  }

  // ステップ2: 初回決済完了 → トークン登録の説明
  if (paymentStep === 'initial_done') {
    return (
      <PageTemplate pageName="subscription-payment" title="お支払い情報の登録">
        <div style={{ padding: '32px 16px', textAlign: 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 16px',
              borderRadius: '50%',
              backgroundColor: '#E8F5E9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: '#4CAF50',
            }}
          >
            ✓
          </div>

          <h2
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '8px',
            }}
          >
            初回のお支払いが完了しました
          </h2>

          <p
            style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '24px',
              lineHeight: 1.6,
            }}
          >
            続けて、毎月の自動引き落としのために
            <br />
            お支払い方法の登録をお願いします。
          </p>

          {/* ステータス */}
          <div
            style={{
              padding: '20px',
              margin: '0 0 24px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>お申し込み</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px' }}>初回お支払い</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '12px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ color: '#FF9800', fontSize: '16px' }}>○</span>
              <span style={{ fontSize: '14px' }}>
                来月以降のお支払い方法登録
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: '#FFF3E0',
                  color: '#E65100',
                }}
              >
                未完了
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 0',
              }}
            >
              {hasCarRegistered ? (
                <>
                  <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
                  <span style={{ fontSize: '14px' }}>車両の本登録</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '12px',
                      color: '#4CAF50',
                      fontWeight: 'bold',
                    }}
                  >
                    完了
                  </span>
                </>
              ) : (
                <>
                  <span style={{ color: '#FF9800', fontSize: '16px' }}>○</span>
                  <span style={{ fontSize: '14px' }}>車両の本登録</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: '#FFF3E0',
                      color: '#E65100',
                    }}
                  >
                    未完了
                  </span>
                </>
              )}
            </div>
          </div>

          {/* 継続決済方法の選択 */}
          <div
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              textAlign: 'left',
              marginBottom: '12px',
            }}
          >
            お支払い方法を選択
          </div>

          {registeredCards.map((card: any) => (
            <div
              key={card.m_payment_method_id || card.paymentMethodId}
              onClick={() => handleActivateWithCard(card)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                marginBottom: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                cursor: isProcessing ? 'wait' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              <i
                className="icon-credit-card"
                style={{ fontSize: '18px', marginRight: '12px', color: '#333' }}
              />
              <div style={{ flex: 1, fontSize: '14px' }}>
                クレジットカード（{card.cardNo || card.card_no}）
              </div>
              <i className="icon-right" style={{ color: '#bbb' }} />
            </div>
          ))}

          <div
            onClick={handleActivateWithPaidy}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              marginBottom: '8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              cursor: isProcessing ? 'wait' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            <img
              src={PAIDY_LOGO_URL}
              alt="ペイディ"
              style={{ width: '50px', marginRight: '12px' }}
            />
            <div style={{ flex: 1, fontSize: '14px' }}>
              あと払い（ペイディ）
            </div>
            <i className="icon-right" style={{ color: '#bbb' }} />
          </div>

          <button
            onClick={async () => {
              try {
                const returnUrl = paths.homeStatus('gmo_success');
                const cancelUrl = paths.homeStatus('gmo_cancel');
                window.localStorage.setItem(
                  'destination_page',
                  paths.subscriptionPayment,
                );
                const response = await addCreditCardPaymentMethod(
                  returnUrl,
                  cancelUrl,
                );
                if (
                  !response ||
                  !response.success ||
                  !('LinkUrl' in response.data)
                ) {
                  f7.dialog.alert('カード登録の準備に失敗しました');
                  return;
                }
                const linkUrl = response.data.LinkUrl;
                if (isApp) {
                  location.href = linkUrl;
                } else {
                  window.location.href = linkUrl;
                }
              } catch (error: any) {
                f7.dialog.alert(
                  error?.message || 'カード登録の処理中にエラーが発生しました',
                );
              }
            }}
            style={{
              width: '100%',
              padding: '12px',
              marginTop: '8px',
              fontSize: '13px',
              color: '#1E55E6',
              backgroundColor: '#fff',
              border: '1px solid #1E55E6',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            新しいクレジットカードを登録
          </button>

          {!hasCarRegistered && (
            <button
              onClick={() => {
                const affiliateId = (store.state as any).customerInfo
                  ?.m_affiliate_id;
                storeDispatch('setRegisterUser', 'registerUser', {
                  ...initialState.registerUser,
                  affiliate_id: Number(affiliateId),
                  currentStep: 3,
                });
                f7.view.main.router.navigate(paths.registerNewMember, {
                  props: {
                    sourcePage: paths.subscriptionPayment,
                    startStep: 3,
                  },
                });
              }}
              style={{
                width: '100%',
                padding: '16px',
                marginTop: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#1E55E6',
                backgroundColor: '#fff',
                border: '1px solid #1E55E6',
                borderRadius: '28px',
                cursor: 'pointer',
              }}
            >
              車検証を登録する
            </button>
          )}
        </div>
      </PageTemplate>
    );
  }

  // ステップ1: 決済方法選択
  return (
    <>
      <PageTemplate
        pageName="subscription-payment"
        title="お支払い情報の入力"
        showNavBack
        handleClickBack={() => f7.view.main.router.back()}
      >
        <div style={{ padding: '16px' }}>
          <div
            style={{
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
            }}
          >
            <div
              style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}
            >
              初回お支払い金額
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
              ¥{displayFee.toLocaleString()}
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              以降、毎月 ¥{displayFee.toLocaleString()}{' '}
              が自動的にお支払いされます
            </div>
          </div>

          <p
            style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '16px',
              lineHeight: 1.6,
            }}
          >
            お支払い方法を選択してください。
          </p>

          {registeredCards.map((card: any) => (
            <div
              key={card.paymentMethodId}
              onClick={() => handleSelectCard(card)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                marginBottom: '8px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                cursor: 'pointer',
              }}
            >
              <i
                className="icon-credit-card"
                style={{ fontSize: '20px', marginRight: '12px', color: '#333' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                  クレジットカード
                </div>
                <div
                  style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}
                >
                  {card.cardNo}
                </div>
              </div>
              <div style={{ color: '#ccc', fontSize: '20px' }}>›</div>
            </div>
          ))}

          {/* 新規クレジットカード登録 */}
          <div
            onClick={async () => {
              try {
                const returnUrl = paths.homeStatus('gmo_success');
                const cancelUrl = paths.homeStatus('gmo_cancel');
                window.localStorage.setItem(
                  'destination_page',
                  paths.subscriptionPayment,
                );
                const response = await addCreditCardPaymentMethod(
                  returnUrl,
                  cancelUrl,
                );
                if (
                  !response ||
                  !response.success ||
                  !('LinkUrl' in response.data)
                ) {
                  f7.dialog.alert('カード登録の準備に失敗しました');
                  return;
                }
                const linkUrl = response.data.LinkUrl;
                if (isApp) {
                  location.href = linkUrl;
                } else {
                  window.location.href = linkUrl;
                }
              } catch (error: any) {
                console.error('GMOカード登録エラー:', error);
                f7.dialog.alert(
                  error?.message || 'カード登録の処理中にエラーが発生しました',
                );
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              marginBottom: '8px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px dashed #e0e0e0',
              cursor: 'pointer',
            }}
          >
            <i
              className="icon-credit-card"
              style={{
                fontSize: '20px',
                marginRight: '12px',
                color: '#1E55E6',
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', color: '#1E55E6' }}>
                新しいクレジットカードを登録
              </div>
            </div>
            <div style={{ color: '#ccc', fontSize: '20px' }}>›</div>
          </div>

          {/* Paidy（都度決済 → 登録済みならStep2スキップ） */}
          <div
            onClick={() =>
              registeredPaidys.length > 0
                ? handleRegisteredPaidy(registeredPaidys[0])
                : handleSelectPaidy()
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              marginBottom: '8px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              cursor: isProcessing ? 'wait' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            <img
              src={PAIDY_LOGO_URL}
              alt="ペイディ"
              style={{ width: '57px', marginRight: '12px' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                あと払い（ペイディ）
              </div>
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}
              >
                メールアドレスと電話番号で簡単登録
              </div>
            </div>
            <div style={{ color: '#ccc', fontSize: '20px' }}>›</div>
          </div>
        </div>
      </PageTemplate>
      {gmoChargeParams && (
        <div style={{ display: 'none' }}>
          <PostChargeDataToGmoForm
            ref={gmoFormRef}
            GmoChargeParams={gmoChargeParams}
          />
        </div>
      )}
    </>
  );
};
